import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EmailIcon from '@mui/icons-material/EmailOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SendIcon from '@mui/icons-material/Send'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Dialog } from '@/components/AppDialog'
import { api } from '@/api/client'
import { ApiError } from '@/api/client'

/**
 * The documents that can be sent, spelled the way the path spells them.
 *
 * <p>The same seven words the server knows. A screen names its own document and
 * gets the button, the dialog and the wording without writing any of it.</p>
 */
export type DocumentKind =
  | 'quotation'
  | 'order-confirmation'
  | 'pre-billing'
  | 'proforma-invoice'
  | 'tax-invoice'
  | 'receipt'
  | 'refund-voucher'
  | 'write-off-voucher'
  | 'menu-sheet'

type SendOptions = {
  kind: string
  label: string
  documentNumber: string
  customerName: string
  email: string | null
  mobile: string | null
  consentEmail: boolean
  consentWhatsapp: boolean
  linkOffered: boolean
  emailLive: boolean
  whatsappLive: boolean
  subject: string
  message: string
}

type SendResult = {
  documentNumber: string
  sent: { channel: string; recipient: string }[]
  link: string | null
}

/**
 * Sending a document to the customer, from wherever it is shown.
 *
 * <p>One dialog for all of them. It opens on what is already known - the
 * customer's own address and number, a subject and a message written from the
 * document - so the ordinary send is a glance and a click, and the rare one can
 * still be edited word by word.</p>
 *
 * <p>Nothing goes out from the browser. The message is queued on the server with
 * the PDF kept beside it, so a mail server that is down delays the message
 * rather than losing it, and what was sent stays on the record.</p>
 */
export function SendDocumentDialog({
  kind,
  id,
  open,
  onClose,
}: {
  kind: DocumentKind
  id: string
  open: boolean
  onClose: () => void
}) {
  const options = useQuery({
    queryKey: ['document-send', kind, id],
    queryFn: () => api.get<SendOptions>(`/api/v1/documents/${kind}/${id}/send`),
    enabled: open,
  })

  const [channels, setChannels] = useState<string[]>(['EMAIL'])
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [includeLink, setIncludeLink] = useState(true)
  const [result, setResult] = useState<SendResult | null>(null)

  // Filled from the document once it arrives, and left alone after that: what
  // somebody has started typing must not be overwritten by a refetch.
  useEffect(() => {
    const loaded = options.data
    if (!loaded || !open) return
    setEmail(loaded.email ?? '')
    setMobile(loaded.mobile ?? '')
    setSubject(loaded.subject)
    setMessage(loaded.message)
    setIncludeLink(loaded.linkOffered)
    setChannels(loaded.email ? ['EMAIL'] : loaded.mobile ? ['WHATSAPP'] : [])
    setResult(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.data, open])

  const send = useMutation({
    mutationFn: () =>
      api.post<SendResult>(`/api/v1/documents/${kind}/${id}/send`, {
        channels,
        email,
        mobile,
        subject,
        message,
        includeLink,
      }),
    onSuccess: setResult,
  })

  const toggle = (channel: string) =>
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((one) => one !== channel)
        : [...current, channel],
    )

  const loaded = options.data
  const failure = send.error instanceof ApiError ? send.error.message : null

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Send {loaded ? loaded.label.toLowerCase() : 'document'}
        {loaded ? ` ${loaded.documentNumber}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {options.isError ? (
          <Alert severity="error">This document cannot be prepared for sending.</Alert>
        ) : null}

        {result ? (
          <Stack spacing={1.5}>
            <Alert severity="success">
              Queued for {result.sent.map((one) => one.recipient).join(' and ')}. It goes out
              within the minute; the inbox shows whether it was delivered.
            </Alert>
            {result.link ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  label="The link the customer was given"
                  value={result.link}
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title="Copy the link">
                  <IconButton onClick={() => navigator.clipboard?.writeText(result.link ?? '')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {loaded ? (
              <Typography variant="body2" color="text.secondary">
                To {loaded.customerName}. The PDF goes with the message.
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1}>
              <Chip
                icon={<EmailIcon />}
                label="Email"
                color={channels.includes('EMAIL') ? 'primary' : 'default'}
                variant={channels.includes('EMAIL') ? 'filled' : 'outlined'}
                onClick={() => toggle('EMAIL')}
                disabled={loaded ? !loaded.consentEmail : false}
              />
              <Chip
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                color={channels.includes('WHATSAPP') ? 'primary' : 'default'}
                variant={channels.includes('WHATSAPP') ? 'filled' : 'outlined'}
                onClick={() => toggle('WHATSAPP')}
                disabled={loaded ? !loaded.consentWhatsapp : false}
              />
            </Stack>

            {/* Said before the send, not discovered after it: with no provider
                connected the message reaches the log and nobody else, and the
                record would still read as sent. */}
            {loaded && ((channels.includes('EMAIL') && !loaded.emailLive)
              || (channels.includes('WHATSAPP') && !loaded.whatsappLive)) ? (
              <Alert severity="warning">
                {!loaded.emailLive && !loaded.whatsappLive
                  ? 'Neither email nor WhatsApp is connected yet'
                  : !loaded.emailLive
                    ? 'Email is not connected yet'
                    : 'WhatsApp is not connected yet'}
                . The message will be recorded and kept, but it will not reach the customer until
                the account is set up.
              </Alert>
            ) : null}

            {loaded && (!loaded.consentEmail || !loaded.consentWhatsapp) ? (
              <Alert severity="info">
                {loaded.customerName} has asked not to be contacted
                {!loaded.consentEmail ? ' by email' : ''}
                {!loaded.consentEmail && !loaded.consentWhatsapp ? ' or' : ''}
                {!loaded.consentWhatsapp ? ' on WhatsApp' : ''}. Change that on the customer if
                they have since agreed.
              </Alert>
            ) : null}

            {channels.includes('EMAIL') ? (
              <TextField
                fullWidth
                label="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                helperText="Sending to a different address does not change the customer's own"
              />
            ) : null}

            {channels.includes('WHATSAPP') ? (
              <TextField
                fullWidth
                label="Mobile number"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                helperText="Ten digits; the country code is added"
              />
            ) : null}

            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              helperText={
                channels.includes('WHATSAPP')
                  ? 'On WhatsApp the PDF goes with the approved Meta template (name, number, amounts filled in). This message is used for email, and on WhatsApp only if the template is refused.'
                  : undefined
              }
            />

            {loaded?.linkOffered ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeLink}
                    onChange={(event) => setIncludeLink(event.target.checked)}
                  />
                }
                label="Include a link the customer can open on a phone"
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                This one goes as an attachment only. A link can be forwarded on, and a tax
                document is for the person it was raised against.
              </Typography>
            )}

            {failure ? <Alert severity="error">{failure}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<LocalPrintshopIcon />}
          onClick={() => void api.print(`/api/v1/documents/${kind}/${id}/pdf`)}
        >
          Print
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => api.download(`/api/v1/documents/${kind}/${id}/pdf`, undefined,
            `${loaded?.documentNumber ?? 'document'}.pdf`)}
        >
          Download PDF
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        {result ? null : (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={channels.length === 0 || send.isPending || !loaded}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

/**
 * The button that opens it, so a screen adds sending in one line.
 */
export function SendDocumentButton({
  kind,
  id,
  label = 'Send',
  size = 'small',
}: {
  kind: DocumentKind
  id: string
  label?: string
  size?: 'small' | 'medium' | 'large'
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size={size} startIcon={<SendIcon />} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? (
        <SendDocumentDialog kind={kind} id={id} open={open} onClose={() => setOpen(false)} />
      ) : null}
    </>
  )
}

/** The same thing where only an icon fits, as in a table row. */
export function SendDocumentIcon({ kind, id }: { kind: DocumentKind; id: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Tooltip title="Send to the customer">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {open ? (
        <SendDocumentDialog kind={kind} id={id} open={open} onClose={() => setOpen(false)} />
      ) : null}
    </>
  )
}
